import React, { useState } from 'react';
import type InputsTable from '@components-shared/configConstructor/inputsTable';
import { keyMap } from '@locales/lokalise/keyMap';
import { IFormField } from './formField.interface';
import validators from './validators';

export type IValidationData = { valid: boolean; message: string; placeholders?: Record<string, string> };
export type IValidationState = { [fieldName: string]: IValidationData | IValidationState };

export interface FormGeneratorValidationProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  form: any;
  validationState: IValidationState;
  setValidationState: (validationState: IValidationState) => void;
  setInitialValidationState: (fields: IFormField[]) => void;
  validate: (validationPath: string) => boolean;
  setInputsTableProp: (classComponent: InstanceType<typeof InputsTable>, fieldName: string) => void;
  checkField: (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    form: any,
    field: IFormField,
  ) => IValidationData;
}

export function withFormGeneratorValidation<P extends FormGeneratorValidationProps>(
  WrappedComponent: React.ComponentType<P>,
  getFieldsFromOuterScope: () => IFormField[],
) {
  const inputsTables: Record<string, InstanceType<typeof InputsTable>> = {};
  const setInputsTableProp = (classComponent: InstanceType<typeof InputsTable>, fieldName: string) =>
    (inputsTables[fieldName] = classComponent);

  const InnerComponent: React.FC<P & { forwardRef?: unknown }> = (props) => {
    const { forwardRef, ...rest } = props;

    const initValidation = (fields: IFormField[]): IValidationState => {
      return fields.reduce<IValidationState>((acc, field) => {
        if (field.type === 'section') {
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          acc[field.name] = initValidation(field.fields!);
        } else {
          acc[field.name] = { valid: true, message: '' };
        }

        return acc;
      }, {});
    };

    const [validationState, setValidationState] = useState(() => initValidation(getFieldsFromOuterScope()));

    const handleSetInitialValidationState = (fields: IFormField[]) => {
      setValidationState(initValidation(fields));
    };

    const startsWithPath = (path: string, pathToCheck: string | string[]) => {
      const pathSegments = path.split('.');

      if (Array.isArray(pathToCheck)) {
        return pathToCheck.some((pathToCheck) => {
          const pathToCheckSegments = pathToCheck.split('.');

          return pathToCheckSegments.every((segment, i) => {
            return segment === pathSegments[i];
          });
        });
      } else {
        const pathToCheckSegments = pathToCheck.split('.');

        return pathToCheckSegments.every((segment, i) => {
          return segment === pathSegments[i];
        });
      }
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const checkField = (form: any, field: IFormField) => {
      if (field.required) {
        if (form[field.name] === '' || form[field.name] === null || form[field.name] === undefined) {
          return { valid: false, message: keyMap.formsValidationRequiredError };
        }

        if (field.type === 'multiselect' && !form[field.name].length) {
          return { valid: false, message: keyMap.formsValidationRequiredError };
        }

        if (field.confirm && form[field.name] !== form[field.confirm]) {
          const message = field.validationMessage || keyMap.formsValidationConfirmError;

          return { valid: false, message };
        }
      }

      if (field.onlyEng) {
        const validator = validators.onlyEngAndDigits;
        const params = { ...field };

        if (!validator.check(form[field.name], params)) {
          return { valid: false, message: validator.message };
        }
      }

      if (field.onlyEngLetters) {
        const validator = validators.onlyEngLetters;
        const params = { ...field };

        if (!validator.check(form[field.name], params)) {
          return { valid: false, message: validator.message };
        }
      }

      if (field.type && validators[field.type]) {
        const validator = validators[field.type];
        const params = { ...field, form };

        // @ts-ignore
        if (!validator.check(form[field.name], params)) {
          return { valid: false, message: validator.message };
        }
      }

      if (field.validator) {
        const { validator } = field;
        const params = { ...field, form };

        if (!validator.check(form[field.name], params)) {
          return { valid: false, message: validator.message, placeholders: validator.placeholders };
        }
      }

      if (field.type === 'array') {
        if (inputsTables[field.name] && !inputsTables[field.name].validate()) {
          return { valid: false, message: keyMap.formsValidationMapError };
        }
      }

      return { valid: true, message: '' };
    };

    const getValidation = (
      fields: IFormField[],
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      form: any,
      sectionPath: string | null,
      validationPath?: string,
    ): IValidationState => {
      return fields.reduce<IValidationState>((validation, field) => {
        const path = sectionPath ? [sectionPath, field.name].join('.') : field.name;

        if (field.type === 'section') {
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          validation[field.name] = getValidation(field.fields!, form[field.name], path, validationPath);
        } else if (validationPath && !startsWithPath(path, validationPath)) {
          validation[field.name] = { valid: true, message: '' };
        } else {
          validation[field.name] = checkField(form, field);
        }

        return validation;
      }, {});
    };

    const getIsValid = (fields: IFormField[], validation: IValidationState): boolean => {
      return fields.reduce<boolean>((_isValid, field) => {
        let isValid = _isValid;

        if (isValid === false) {
          return isValid;
        }

        if (isHidden({ props }, field)) {
          return true;
        }

        if (field.type === 'hidden') {
          return true;
        }

        if (field.type === 'section') {
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          isValid = getIsValid(field.fields!, validation[field.name] as IValidationState);
        } else {
          isValid = validation[field.name].valid as boolean;
        }

        return isValid;
      }, true);
    };

    const validate = (validationPath?: string): boolean => {
      const fields = getFieldsFromOuterScope();
      const { form } = props;
      const validation = getValidation(fields, form, null, validationPath);
      const isValid = getIsValid(fields, validation);

      setValidationState(validation);

      if (!isValid) {
        scrollToFirstError(fields, validation);
      }

      return isValid;
    };

    return (
      <WrappedComponent
        {...(rest as P)}
        ref={forwardRef}
        validationState={validationState}
        setValidationState={setValidationState}
        setInitialValidationState={handleSetInitialValidationState}
        validate={validate}
        setInputsTableProp={setInputsTableProp}
        checkField={checkField}
      />
    );
  };

  return React.forwardRef((props: P, ref) => <InnerComponent {...props} forwardRef={ref} />);
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isHidden = (ctx: { props: any }, field: IFormField) => {
  return field.isHidden === true || (typeof field.isHidden === 'function' && field.isHidden(ctx));
};

const scrollToFirstError = (fields: IFormField[], validation: IValidationState) => {
  const getInvalidFieldName = (fields: IFormField[], validation: IValidationState): string => {
    return fields.reduce<string>((name, field) => {
      if (name) {
        return name;
      }

      if (field.type === 'section') {
        return getInvalidFieldName(field.fields as IFormField[], validation[field.name] as IValidationState) as string;
      }

      if (!validation[field.name].valid) {
        return field.name;
      }

      return name;
    }, '');
  };

  const invalidFiledName = getInvalidFieldName(fields, validation);
  const element = document.querySelector(`[data-fieldname=${invalidFiledName}]`) as HTMLElement | null;

  if (element) {
    const closestParentModalNode = element.closest('.modal');

    // ScrollTo is not supported for old mobile browsers.
    // ScrollIntoView has better support, but still not perfect

    if (closestParentModalNode) {
      if (closestParentModalNode.scrollTo) {
        closestParentModalNode.scrollTo({
          top: element.offsetTop,
          behavior: 'smooth',
        });
      } else if (closestParentModalNode.scrollIntoView) {
        closestParentModalNode.scrollIntoView();
      }
    } else {
      const boundingClientRect = element.getBoundingClientRect();

      if (window.scrollTo) {
        window.scrollTo({
          top: window.pageYOffset + boundingClientRect.top - 80,
          behavior: 'smooth',
        });
      } else if (element.scrollIntoView) {
        element.scrollIntoView();
      }
    }
  }
};
